import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { FileText, Eye, Printer } from 'lucide-react';

import API_URL from '../utils/api';

const MyPayslips = () => {
    const [payslips, setPayslips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewingPayslip, setViewingPayslip] = useState(null);

    useEffect(() => {
        fetchPayslips();
    }, []);

    const fetchPayslips = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/payroll`, { withCredentials: true });
            setPayslips(data);
        } catch (error) {
            logger.error('Error fetching payslips:', error);
        } finally {
            setLoading(false);
        }
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const printPayslip = () => {
        window.print();
    };

    const handleDownloadPayslip = async (payrollId) => {
        try {
            const response = await axios.get(`${API_URL}/api/payroll/${payrollId}/payslip-pdf`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payslip_${payrollId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Error downloading payslip');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading your payslips...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="my-payslips-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    My Payslips
                </h1>
                <p className="text-slate-500 mt-1">View and download your salary statements</p>
            </div>

            {/* Payslips Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {payslips.map((payslip) => (
                    <Card key={payslip._id} className="rounded-none border-slate-200 hover:border-blue-300 transition-colors" data-testid={`payslip-card-${payslip._id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-blue-100">
                                    <FileText className="h-6 w-6 text-brand" />
                                </div>
                                <Badge className={`rounded-none ${payslip.status === 'processed' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                    {payslip.status}
                                </Badge>
                            </div>
                            <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                {months[payslip.month - 1]} {payslip.year}
                            </h3>
                            <div className="mt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Gross Salary</span>
                                    <span className="font-medium text-slate-900">${payslip.gross_salary?.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Deductions</span>
                                    <span className="font-medium text-red-600">-${payslip.total_deductions?.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-200 pt-2">
                                    <span className="font-bold text-slate-900">Net Salary</span>
                                    <span className="font-bold text-green-600">${payslip.net_salary?.toLocaleString()}</span>
                                </div>
                            </div>
                            <Button
                                className="w-full mt-4 bg-brand hover:bg-brand rounded-none"
                                onClick={() => setViewingPayslip(payslip)}
                                data-testid={`view-payslip-btn-${payslip._id}`}
                            >
                                <Eye className="h-4 w-4 mr-2" />
                                View Payslip
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full mt-2 rounded-none"
                                onClick={() => handleDownloadPayslip(payslip._id)}
                                data-testid={`download-payslip-btn-${payslip._id}`}
                            >
                                <Printer className="h-4 w-4 mr-2" />
                                Download PDF
                            </Button>
                        </CardContent>
                    </Card>
                ))}
                {payslips.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                        No payslips available yet.
                    </div>
                )}
            </div>

            {/* Payslip Modal */}
            <Dialog open={!!viewingPayslip} onOpenChange={() => setViewingPayslip(null)}>
                <DialogContent className="rounded-none max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <FileText className="h-5 w-5 text-brand" />
                            Payslip
                        </DialogTitle>
                    </DialogHeader>
                    {viewingPayslip && (
                        <div className="mt-4 space-y-6" data-testid="payslip-detail-modal">
                            {/* Company Header */}
                            <div className="text-center border-b border-slate-200 pb-4">
                                <h2 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    DIGI-LOANS
                                </h2>
                                <p className="text-slate-500">HR & Payroll Department</p>
                                <p className="text-lg font-bold mt-2">
                                    Payslip for {months[viewingPayslip.month - 1]} {viewingPayslip.year}
                                </p>
                            </div>

                            {/* Employee Details */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">Employee Name</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.employee_name}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Employee ID</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.employee_code}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Department</p>
                                    <p className="font-medium text-slate-900">{viewingPayslip.department}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Pay Period</p>
                                    <p className="font-medium text-slate-900">{months[viewingPayslip.month - 1]} {viewingPayslip.year}</p>
                                </div>
                            </div>

                            {/* Earnings & Deductions */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-3">Earnings</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Basic Salary</span>
                                            <span className="font-medium">${viewingPayslip.basic_salary?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Allowances</span>
                                            <span className="font-medium">${viewingPayslip.allowances?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Overtime ({viewingPayslip.overtime_hours}hrs)</span>
                                            <span className="font-medium">${viewingPayslip.overtime_pay?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Bonus</span>
                                            <span className="font-medium">${viewingPayslip.bonus?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                                            <span>Gross Salary</span>
                                            <span className="text-green-600">${viewingPayslip.gross_salary?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2 mb-3">Deductions</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Tax</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.tax?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Deductions</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.deductions?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Loan Deduction</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.loan_deduction?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-600">Advance Deduction</span>
                                            <span className="font-medium text-red-600">-${viewingPayslip.advance_deduction?.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
                                            <span>Total Deductions</span>
                                            <span className="text-red-600">-${viewingPayslip.total_deductions?.toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Net Salary */}
                            <div className="bg-brand p-4 -mx-6 -mb-6 flex justify-between items-center">
                                <span className="text-white font-bold text-lg">Net Salary</span>
                                <span className="text-white font-black text-2xl" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    ${viewingPayslip.net_salary?.toLocaleString()}
                                </span>
                            </div>

                            <div className="flex justify-end pt-4 no-print">
                                <Button onClick={printPayslip} className="bg-brand hover:bg-brand rounded-none" data-testid="print-payslip-btn">
                                    <Printer className="h-4 w-4 mr-2" />
                                    Print Payslip
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MyPayslips;
